import React from "react";
import { Dialog, DialogContent, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import type { DialogProps } from "../../misc/DialogProps";
import { DialogCloseButton } from "../../misc/DialogCloseButton";
import { StudentDetailsWithTabs } from "../../details/StudentDetails";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { Row } from "../../misc/Row";
import { DunningProcessTimeline } from "./DunningProcessTimeline";
import { Identifier, RecordContextProvider, useGetOne } from "react-admin";
import { EventPropagationStopper } from "../../misc/EventPropagationStopper";
import { useMenu } from "../../hooks/useMenu";
import { PostponeDunningProcessButton } from "../../buttons/PostponeDunningProcessButton";
import { DialogTopRightActions } from "../../misc/DialogTopRightActions";

export function DunningStudentDialog({
  studentId,
  actions,
  ...dialogProps
}: DialogProps & { studentId: Identifier; actions: React.ReactNode[] }) {
  const { data: student } = useGetOne("students", { id: studentId }, { enabled: !!studentId });
  return (
    <Dialog {...dialogProps} maxWidth={false} fullWidth>
      <DialogCloseButton onClick={dialogProps.onClose} />
      <DialogContent sx={{ backgroundColor: "background.default", pt: 8 }}>
        {student ? (
          <RecordContextProvider value={student}>
            <DialogActions closeDialog={dialogProps.onClose} actions={actions} />
            <DunningProcessTimeline />
            <StudentDetailsWithTabs student={student} />
          </RecordContextProvider>
        ) : (
          <Row minHeight="80px" pt="80px">
            <LoadingIndicator />
          </Row>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DialogActions({ actions, closeDialog }: { actions: React.ReactNode[]; closeDialog: DialogProps["onClose"] }) {
  const { anchorEl, open, openMenu, closeMenu } = useMenu();

  return (
    <EventPropagationStopper>
      <DialogTopRightActions>
        <PostponeDunningProcessButton onSuccess={closeDialog} />
        {actions.length > 0 && (
          <IconButton onClick={openMenu}>
            <MoreVertIcon sx={{ color: "text.secondary" }} />
          </IconButton>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          MenuListProps={{ "aria-labelledby": "dialog-actions" }}
        >
          {actions.map((action, i) => (
            <MenuItem key={i} sx={{ p: 0 }} onClick={closeMenu}>
              {action}
            </MenuItem>
          ))}
        </Menu>
      </DialogTopRightActions>
    </EventPropagationStopper>
  );
}
